import { computed, inject, Injectable, signal } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { ProsApiService, Pro, ProDetail, BlacklistedPro } from './pros-api.service';
import { Auth } from 'src/providers/auth/auth';

export const prosKeys = {
  all: ['pros'] as const,
  lists: () => [...prosKeys.all, 'list'] as const,
  listWithIncludes: () => [...prosKeys.all, 'listWithIncludes'] as const,
  listByAddress: (addressId: number) => [...prosKeys.lists(), 'address', addressId] as const,
  privatePros: () => [...prosKeys.all, 'private'] as const,
  details: () => [...prosKeys.all, 'detail'] as const,
  detail: (proId: number, addressId: number) => [...prosKeys.details(), proId, addressId] as const,
  blacklisted: () => [...prosKeys.all, 'blacklisted'] as const,
};

@Injectable({ providedIn: 'root' })
export class ProsQueriesService {
  private readonly api = inject(ProsApiService);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();

  readonly selectedProId = signal<number | null>(null);
  readonly selectedAddressId = signal<number | null>(null);

  // Main query - the rate-limited endpoint with full includes
  readonly allProsQuery = injectQuery(() => ({
    queryKey: prosKeys.listWithIncludes(),
    queryFn: () => lastValueFrom(this.api.getAllPros()),
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
    enabled: this.auth.isAuthenticated(),
  }));

  // Computed signals for easy access
  readonly allPros = computed(() => this.allProsQuery.data() ?? []);
  readonly isLoadingPros = computed(() => this.allProsQuery.isPending());
  readonly isFetchingPros = computed(() => this.allProsQuery.isFetching());
  readonly prosError = computed(() => this.allProsQuery.error());

  // Blacklisted pros query
  readonly blacklistedProsQuery = injectQuery(() => ({
    queryKey: prosKeys.blacklisted(),
    queryFn: () => lastValueFrom(this.api.getBlacklistedPros()),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: this.auth.isAuthenticated(),
  }));

  readonly blacklistedPros = computed(() => this.blacklistedProsQuery.data() ?? []);

  // Mutations
  readonly addProMutation = injectMutation(() => ({
    mutationFn: (params: any) => lastValueFrom(this.api.addNewPro(params)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: prosKeys.lists() });
      this.queryClient.invalidateQueries({ queryKey: prosKeys.listWithIncludes() });
      this.queryClient.invalidateQueries({ queryKey: prosKeys.privatePros() });
    },
  }));

  readonly blacklistProMutation = injectMutation(() => ({
    mutationFn: (proIds: number[]) => lastValueFrom(this.api.blacklistPro(proIds)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  }));

  readonly unblacklistProMutation = injectMutation(() => ({
    mutationFn: (proId: number) => lastValueFrom(this.api.unblacklistPro(proId)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  }));

  readonly updateProInfoMutation = injectMutation(() => ({
    mutationFn: ({ proId, profile }: { proId: number; profile: Partial<{ name: string; email: string; phone: string }> }) =>
      lastValueFrom(this.api.updateProInfo(proId, profile)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: prosKeys.lists() });
      this.queryClient.invalidateQueries({ queryKey: prosKeys.listWithIncludes() });
    },
  }));

  readonly removeProFromAddressListMutation = injectMutation(() => ({
    mutationFn: ({ addressId, payload }: { addressId: number; payload: any }) =>
      lastValueFrom(this.api.removeProFromAddressList(addressId, payload)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: prosKeys.lists() });
      this.queryClient.invalidateQueries({ queryKey: prosKeys.listWithIncludes() });
    },
  }));

  // Helper methods
  selectPro(proId: number | null): void {
    this.selectedProId.set(proId);
  }

  selectAddress(addressId: number | null): void {
    this.selectedAddressId.set(addressId);
  }

  invalidateAllPros(): void {
    this.queryClient.invalidateQueries({ queryKey: prosKeys.listWithIncludes() });
    this.queryClient.invalidateQueries({ queryKey: prosKeys.lists() });
  }

  invalidateBlacklistedPros(): void {
    this.queryClient.invalidateQueries({ queryKey: prosKeys.blacklisted() });
  }

  // Force refresh
  async refetch(): Promise<void> {
    await this.allProsQuery.refetch();
  }

  // Legacy compatibility: Promise-based method for existing code
  async getAllPros(): Promise<Pro[]> {
    // Use fetchQuery to respect cache - returns cached data if fresh, fetches if stale
    return this.queryClient.fetchQuery({
      queryKey: prosKeys.listWithIncludes(),
      queryFn: () => lastValueFrom(this.api.getAllPros()),
      staleTime: 3 * 60 * 1000,
    });
  }

  // Legacy compatibility: Get all private pros
  async getAllPrivatePros(): Promise<Pro[]> {
    const allPros = await this.getAllPros();
    const privatePros = allPros.filter(pro => pro?.is_private);
    privatePros.sort((a, b) => a.name?.localeCompare(b.name));
    return privatePros;
  }

  // Legacy compatibility: Get pros by address (filters from cached data)
  async getAllProsByAddress(addressId: number): Promise<Pro[]> {
    const allPros = await this.queryClient.fetchQuery({
      queryKey: prosKeys.listWithIncludes(),
      queryFn: () => lastValueFrom(this.api.getAllProsWithPriorities()),
      staleTime: 3 * 60 * 1000,
    });
    const prosInAddress: Pro[] = [];
    allPros.forEach(pro => {
      const filteredPriorities = (pro.address_homekeeper_priorities ?? []).filter(
        priority => priority.address_id === addressId
      );
      if (filteredPriorities.length > 0) {
        const proCopy = { ...pro, address_homekeeper_priorities: filteredPriorities };
        if (!prosInAddress.some(addedPro => addedPro.id === proCopy.id)) {
          prosInAddress.push(proCopy);
        }
      }
    });
    return prosInAddress;
  }

  // Legacy compatibility: Get private pros by address
  async getAllPrivateProsByAddress(addressId?: number): Promise<Pro[]> {
    const id = addressId || parseInt(localStorage.getItem('addressId') || '0');
    const pros = await this.queryClient.fetchQuery({
      queryKey: prosKeys.listByAddress(id),
      queryFn: () => lastValueFrom(this.api.getAllProsByAddressFilter(id)),
      staleTime: 3 * 60 * 1000,
    });
    return pros.filter(pro => pro.is_private);
  }

  // Legacy compatibility: Get blacklisted pros
  async getBlacklistedPros(): Promise<BlacklistedPro[]> {
    return this.queryClient.fetchQuery({
      queryKey: prosKeys.blacklisted(),
      queryFn: () => lastValueFrom(this.api.getBlacklistedPros()),
      staleTime: 3 * 60 * 1000,
    });
  }

  // Legacy compatibility: Get pro detail
  async getProDetail(proId: number, addressId?: number): Promise<ProDetail> {
    const id = addressId ?? parseInt(localStorage.getItem('addressId') || '0');
    return this.queryClient.fetchQuery({
      queryKey: prosKeys.detail(proId, id),
      queryFn: () => lastValueFrom(this.api.getProDetail(proId, id)),
      staleTime: 3 * 60 * 1000,
    });
  }

  // Legacy compatibility: Get pros excluding an address
  async getProsExcludingAddress(addressId?: number): Promise<Pro[]> {
    const id = addressId || parseInt(localStorage.getItem('addressId') || '0');
    return lastValueFrom(this.api.getProsExcludingAddress(id));
  }

  // Get pro by ID from cached data
  getProById(proId: number): Pro | undefined {
    return this.allPros().find(pro => pro.id === proId);
  }

  // Check if user has private pros
  async checkIfHasPrivatePro(proListResponse: Pro[] | null = null): Promise<boolean> {
    const hasPrivateProValue = localStorage.getItem('hasPrivatePro');
    if (hasPrivateProValue !== null) {
      return hasPrivateProValue === 'true';
    }
    const proList = proListResponse || await this.getAllPrivatePros();
    const hasPrivatePro = proList.length > 0;
    localStorage.setItem('hasPrivatePro', hasPrivatePro ? 'true' : 'false');
    return hasPrivatePro;
  }

  // Get all non-private pros
  async getAllNoPrivatePros(): Promise<Pro[]> {
    const allPros = await this.getAllPros();
    return allPros.filter(pro => !pro?.is_private);
  }

  // Check if has non-private pros
  async checkIfHasNoPrivatePros(): Promise<boolean> {
    const pros = await this.getAllNoPrivatePros();
    return pros.length > 0;
  }
}
